import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { actions as ratingsActions } from '@reducers/ratings';
import { actions as accountsActions } from '@reducers/Account';
import { accountSelector, isLoadingAccountSelector } from 'selectors/Account';
import { Loading } from 'Common/components/Atoms/Loading';
import {
  Div,
  Flex,
  FontIcon,
  Input,
  InputArea,
  Link,
  StarRating,
  StandardContainer,
  CenteredContainer,
  Col,
} from 'Common';
import { Text } from 'Common/components/Atoms/Text';
import { faComment } from '@fortawesome/pro-regular-svg-icons';
import PropTypes from 'prop-types';
import withTheme from '@mui/styles/withTheme';
import withStyles from '@mui/styles/withStyles';
import queryString from 'qs';
import RatingConfirmation from '../RatingConfirmation';
import { setDestIfNeeded } from '../../utils/intendedDest';
import UnauthedHeader from '../../components/UnauthedHeader';

export const inlineStyles = (theme) => ({
  footerLink: {
    color: theme.palette.secondary.main,
    margin: '0 3px 0 3px',
  },
  feedbackDescription: {
    width: '80%',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  negativeChecklist: {
    marginTop: theme.spacing(1),
  },
});

const additionalPositiveFeedbackPlaceholder =
  'e.g. Really great feedback running the scale from very granular ' +
  'to overall structure and quality. Thanks! ' +
  "It's particularly useful for us when PullRequest points out...";
const additionalNegativeFeedbackPlaceholder =
  "e.g. We've gotten complaints from our users that our app is slow. " +
  'Please focus on performance optimizations...';

export const pages = [
  'CHECKLIST',
  'DISLIKED_REVIEWERS',
  'LIKED_REVIEWERS',
  'ADDITIONAL_FEEDBACK',
  'STAR_RATING',
];

export class Rating extends React.Component {
  state = {
    reasons: {},
    likedReviewers: null,
    dislikedReviewers: null,
    comments: null,
    stars: null,
    page: pages[0],
    showOtherReasonInput: false,
    needsConfirmation: !this.props.isLoggedIn,
    helpfulnessChanged: false,
  };

  submitFeedback = () => {
    const { reasons, likedReviewers, dislikedReviewers, comments } = this.state;
    const { ratingUuid, helpful, ratingType, postFeedback } = this.props;

    let reasonList = null;
    if (Object.keys(reasons).length > 0) {
      reasonList = Object.values(reasons).join(', ');
    }

    postFeedback(
      ratingUuid,
      helpful,
      ratingType,
      reasonList,
      likedReviewers,
      dislikedReviewers,
      comments
    );

    this.nextPage();
  };

  submitStarRating = (stars) => {
    this.setState({ stars });
    const { ratingUuid, helpful, ratingType, postRating } = this.props;

    postRating(ratingUuid, helpful, ratingType, stars);
  };

  handleAdditionalFeedbackChange = (event) => {
    this.setState({ comments: event.target.value || null });
  };

  handleLikedReviewersChange = (event) => {
    this.setState({ likedReviewers: event.target.value || null });
  };

  handleDislikedReviewersChange = (event) => {
    this.setState({ dislikedReviewers: event.target.value || null });
  };

  handleReasonCheckboxChange = (event) => {
    let { reasons } = this.state;
    const reasonKey = event.target.name;

    if (event.target.checked) {
      reasons[reasonKey] = event.target.value;
    } else {
      delete reasons[reasonKey];
    }

    this.setState({ reasons });
  };

  handleOtherReasonChange = (event) => {
    let { reasons } = this.state;

    reasons['reasons-other'] = event.target.value;

    this.setState({ reasons });
  };

  nextPage() {
    const { page } = this.state;
    const { history, showFeedbackModal, helpful } = this.props;
    const nextPage = Object.values(pages).indexOf(page) + 1;

    if (!helpful && nextPage < pages.length) {
      this.setState({ page: pages[nextPage] });
    } else {
      showFeedbackModal(helpful);
      history.push('/dash');
    }
  }

  onLastPage() {
    const { page } = this.state;
    return Object.values(pages).indexOf(page) === pages.length - 1;
  }

  continueButtonDisabled() {
    const {
      page,
      reasons,
      dislikedReviewers,
      likedReviewers,
      comments,
      stars,
    } = this.state;

    switch (page) {
      case 'CHECKLIST':
        return Object.values(reasons).length === 0;
      case 'DISLIKED_REVIEWERS':
        return dislikedReviewers === null;
      case 'LIKED_REVIEWERS':
        return likedReviewers === null;
      case 'ADDITIONAL_FEEDBACK':
        return comments === null;
      case 'STAR_RATING':
        return stars === null;
      default:
        return false;
    }
  }

  receivedConfirmation = (helpfulnessChanged) => {
    this.setState({
      needsConfirmation: false,
      helpfulnessChanged,
    });
  };

  getProviderQuery = () => {
    const { provider } = this.props;
    return provider !== 'onprem' ? '?provider=' + provider : '';
  };

  renderMistakenClick() {
    const { ratingUuid, helpful, ratingType, classes } = this.props;
    const redirect = helpful ? 'false' : true;
    return (
      <Flex center justifyContent="center" m="2rem 0 1rem 0">
        {'Was this a mistake? '}{' '}
        <Link
          href={`/reviews/ratings/${ratingUuid}/${redirect}/${ratingType}`}
          className={classes.footerLink}
        >
          Click here
        </Link>{' '}
        to rate this review as {helpful ? 'un' : ''}helpful instead.
      </Flex>
    );
  }

  renderNegativeChecklist() {
    const { classes } = this.props;
    const { showOtherReasonInput } = this.state;

    const checkboxes = [
      "The reviewers weren't thorough enough. The feedback was too surface-level.",
      'The turnaround time to get this reviewed was too long.',
      "I got a lot of feedback for things I don't care about.",
      'Some of the feedback from PullRequest was wrong or inaccurate.',
    ];

    return (
      <div>
        Select all that apply.
        <form className={classes.negativeChecklist}>
          {checkboxes.map((checkbox, index) => {
            return (
              <div key={checkbox}>
                <Input
                  mt="1rem"
                  type="checkbox"
                  width="fit-content"
                  height="fit-content"
                  name={'reasons-' + index}
                  value={checkbox}
                  onChange={this.handleReasonCheckboxChange}
                />
                {' ' + checkbox}
              </div>
            );
          })}
          <Input
            mt="1rem"
            type="checkbox"
            width="fit-content"
            height="fit-content"
            name="reasons-other"
            onChange={(event) => {
              this.handleReasonCheckboxChange(event);
              this.setState({ showOtherReasonInput: !showOtherReasonInput });
            }}
          />{' '}
          Other
          {showOtherReasonInput && (
            <InputArea
              mt="1rem"
              fontSize="13px"
              onChange={this.handleOtherReasonChange}
            />
          )}
        </form>
      </div>
    );
  }

  renderReviewerNamesInput = (onChange) => {
    return (
      <div>
        If so, please let us know.
        <InputArea
          mt="1rem"
          mb="2rem"
          fontSize="13px"
          minRows="1"
          onChange={(event) => {
            onChange(event);
          }}
          placeholder="e.g. Tim P, Kara, Nick H..."
        />
      </div>
    );
  };

  renderPositiveRating = () => {
    const { classes, theme } = this.props;

    return (
      <StandardContainer
        variant="no-title"
        showSecondaryButton={false}
        primaryButtonLabel="Submit"
        primaryButtonTextColor={theme.palette.common.white}
        onClickPrimaryButton={this.submitFeedback}
        m={0}
        p="2rem 0 0 0"
        contentsWidth="100%"
      >
        <Flex direction="column" justifyContent="center" alignItems="center">
          <Text variant="h4">You{"'"}ve rated this review helpful.</Text>
          <br />
          <Div className={classes.feedbackDescription}>
            We{"'"}re glad you had a great experience! Let us know more about
            it!
          </Div>
        </Flex>
        <Flex direction="row">
          <FontIcon
            icon={faComment}
            size="2x"
            color={theme.palette.secondary.main}
            mx="11px" // since the question icon is 26x26, 11+26+11 = 48px = 3rem (next Div's mr)
          />
          <Div width="100%" mr="3rem">
            <Text variant="h4" mb={theme.spacing(1)}>
              How did the reviewers do?
            </Text>
            <Div m="0.5rem 0 2rem">
              <StarRating
                starSize="3x"
                onChange={(value) => {
                  this.submitStarRating(value);
                }}
              />
            </Div>
          </Div>
        </Flex>
        <Flex direction="row">
          <FontIcon
            icon={faComment}
            size="2x"
            color={theme.palette.secondary.main}
            mx="11px" // since the question icon is 26x26, 11+26+11 = 48px = 3rem (next Div's mr)
          />
          <Div width="100%" mr="3rem">
            <Text variant="h4" mb={theme.spacing(1)}>
              Is there anything you{"'"}d like us to focus on more in your
              reviews?
            </Text>
            We want to make sure your experience with PullRequest is
            consistently great.
            <InputArea
              mt="1rem"
              fontSize="13px"
              onChange={this.handleAdditionalFeedbackChange}
              placeholder={additionalPositiveFeedbackPlaceholder}
            />
          </Div>
        </Flex>
        {this.renderMistakenClick()}{' '}
      </StandardContainer>
    );
  };

  renderNegativeRating = () => {
    const { page } = this.state;
    const { theme, classes } = this.props;

    let question = '';
    switch (page) {
      case 'CHECKLIST':
        question = 'What went wrong?';
        break;
      case 'DISLIKED_REVIEWERS':
        question =
          'Are there any reviewers you consistently have problems with?';
        break;
      case 'LIKED_REVIEWERS':
        question = 'Are there any reviewers you particularly like?';
        break;
      case 'ADDITIONAL_FEEDBACK':
        question = 'Additional feedback';
        break;
      case 'STAR_RATING':
      default:
        question = 'How would you rate this review?';
    }

    return (
      <StandardContainer
        variant="no-title"
        showSecondaryButton={true}
        secondaryButtonColor={theme.palette.highlight.main}
        secondaryButtonTextColor={theme.palette.common.white}
        secondaryButtonLabel="Skip"
        onClickSecondaryButton={() => this.nextPage()}
        primaryButtonColor={
          this.onLastPage()
            ? theme.palette.success.main
            : theme.palette.secondary.main
        }
        primaryButtonLabel={this.onLastPage() ? 'Submit' : 'Continue'}
        primaryButtonDisabled={this.continueButtonDisabled()}
        primaryButtonTextColor={theme.palette.common.white}
        onClickPrimaryButton={this.submitFeedback}
        m={0}
        p="2rem 0 0 0"
        contentsWidth="100%"
      >
        {page === 'CHECKLIST' && (
          <Flex direction="column" justifyContent="center" alignItems="center">
            <Text variant="h4">You{"'"}ve rated this review unhelpful.</Text>
            <br />
            <Div className={classes.feedbackDescription}>
              We{"'"}re committed to providing you with a consistently great
              experience. Any information you{"'"}re able to provide will help
              us take action quickly to ensure you{"'"}re getting the very most
              out of PullRequest.
            </Div>
          </Flex>
        )}
        <Flex direction="row">
          <FontIcon
            icon={faComment}
            size="2x"
            color={theme.palette.secondary.main}
            mx="11px" // since the question icon is 26x26, 11+26+11 = 48px = 3rem (next Div's mr)
          />
          <Div width="100%" mr="3rem">
            <Text variant="h4" mb={theme.spacing(1)}>
              {question}
            </Text>
            {page === 'CHECKLIST' && this.renderNegativeChecklist()}
            {page === 'DISLIKED_REVIEWERS' &&
              this.renderReviewerNamesInput(this.handleDislikedReviewersChange)}
            {page === 'LIKED_REVIEWERS' &&
              this.renderReviewerNamesInput(this.handleLikedReviewersChange)}
            {page === 'ADDITIONAL_FEEDBACK' && (
              <div>
                We{"'"}ll use any information you provide to help make sure your
                experience with PullRequest is consistently great.
                <InputArea
                  mt="1rem"
                  mb="2rem"
                  fontSize="13px"
                  onChange={this.handleAdditionalFeedbackChange}
                  placeholder={additionalNegativeFeedbackPlaceholder}
                />
              </div>
            )}
            {page === 'STAR_RATING' && (
              <Div m="0.5rem 0 2rem 0">
                <StarRating
                  starSize="3x"
                  onChange={(value) => {
                    this.submitStarRating(value);
                  }}
                />
              </Div>
            )}
          </Div>
        </Flex>
        {page === 'CHECKLIST' && this.renderMistakenClick()}
      </StandardContainer>
    );
  };

  setIntendedDestination = () => {
    const { helpful, ratingUuid, ratingType } = this.props;
    const { helpfulnessChanged } = this.state;
    // If on confirmation, they choose the opposite helpful
    // make sure rerouted to correct rating page on login
    const wasHelpful = !helpfulnessChanged ? String(helpful) : String(!helpful);
    const ratingTypeStr = ratingType ? `/${ratingType}` : '';
    const providerQuery = this.getProviderQuery();
    setDestIfNeeded(
      `/dash/ratings/${ratingUuid}/${wasHelpful}${ratingTypeStr}${providerQuery}`
    );
  };

  /**
   * If user is not logged in check to see if they confirmed rating
   *
   * If the user is not logged in and the rating still needs to be confirmed
   * send user to ratings confirmation page
   *
   * Otherwise send them to required feedback page
   */
  render() {
    const { helpful, isLoggedIn, isLoadingAccount, theme } = this.props;
    const { needsConfirmation } = this.state;
    if (isLoadingAccount) {
      return (
        <Div>
          <UnauthedHeader />
          <Flex p="2rem">
            <Col alignItems="center" width="100%" mt={10}>
              <Loading size={48} />
            </Col>
          </Flex>
        </Div>
      );
    }

    if (!needsConfirmation && !isLoggedIn) {
      this.setIntendedDestination();
      const redirect = '/login' + this.getProviderQuery();
      return <Redirect to={redirect} />;
    }

    return (
      <>
        {needsConfirmation && !isLoggedIn ? (
          <RatingConfirmation
            {...this.props}
            receivedConfirmation={this.receivedConfirmation}
          />
        ) : (
          <CenteredContainer
            width="40rem"
            title="Thanks for your feedback"
            pagePadding="1rem"
            m="1rem 0"
            p="0"
          >
            {helpful
              ? this.renderPositiveRating()
              : this.renderNegativeRating()}
          </CenteredContainer>
        )}
      </>
    );
  }
}

Rating.propTypes = {
  history: PropTypes.object.isRequired,
  ratingUuid: PropTypes.string.isRequired,
  helpful: PropTypes.bool.isRequired,
  ratingType: PropTypes.string,
  provider: PropTypes.string,
  hasConfirmed: PropTypes.bool,
  isLoadingRating: PropTypes.bool.isRequired,
  isLoadingAccount: PropTypes.bool.isRequired,
  postRating: PropTypes.func.isRequired,
  postFeedback: PropTypes.func.isRequired,
  showFeedbackModal: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  theme: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  integrations: PropTypes.object,
};

Rating.defaultProps = {
  hasConfirmed: false,
};

function mapStateToProps(state, ownProps) {
  const { ratings } = state;
  const account = accountSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  const routeParams = ownProps.match.params;
  const ratingUuid = routeParams.uuid;
  const helpful = routeParams.helpful === 'true';
  const ratingType = routeParams.ratingType || 'single';
  const qs = queryString.parse(ownProps.location.search, {
    ignoreQueryPrefix: true,
  });
  const provider = qs.provider;
  return {
    ratingUuid,
    helpful,
    ratingType,
    provider,
    hasConfirmed: ratings.hasConfirmed ?? false,
    isLoadingRating: ratings.isLoading,
    isLoadingAccount,
    isLoggedIn: !!account.user,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...ratingsActions,
      ...accountsActions,
    },
    dispatch
  );
}

export default withStyles(inlineStyles)(
  withTheme(connect(mapStateToProps, mapDispatchToProps)(Rating))
);
